import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'

async function getInvite(code: string): Promise<InviteDto> {
  const response = await fetch(`/api/invites/${code}`)

  if (!response.ok) {
    const errorText: { error: string } = await response.json()
    throw new Error(`Error fetching invite: ${errorText.error}`)
  }

  return ((await response.json()) as InviteDto)!
}

async function acceptInvite(code: string) {
  const response = await fetch(`/api/invites/${code}/accept`, {
    method: 'POST',
  })

  if (!response.ok) {
    throw new Error('Error accepting invite')
  }
}

async function createInvite(
  email: string,
  role: 'organization' | 'technician',
  organization: number,
) {
  const response = await fetch(`/api/organizations/${organization}/invites`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, role }),
  })

  if (!response.ok) {
    try {
      const error: { error: string } = await response.json()
      throw new Error('Failed to create invite: ' + error.error)
    } catch {
      const errorMessage = await response.text()
      throw new Error('Failed to create invite: ' + errorMessage)
    }
  }

  return (await response.json())!
}

export const useInvite = (
  code: string,
  queryOptions?: Partial<
    UseQueryOptions<InviteDto, Error, InviteDto, [string, string]>
  >,
) => {
  return useQuery({
    queryKey: ['invite', code],
    queryFn: () => getInvite(code),
    ...queryOptions,
  })
}

export const useAcceptInviteMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (code: string) => acceptInvite(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
    },
  })
}

export const useCreateInviteMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      email,
      role,
      organization,
    }: {
      email: string
      role: 'organization' | 'technician'
      organization: number
    }) => createInvite(email, role, organization),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
    },
  })
}
